import {
  createContext,
  useContext,
  useMemo,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useEdgesState, useNodesState } from "reactflow";
import { getConnectedChildNodes, getConnectedNodesWithSource, InitialNodesEdgesByType } from "../../utilities/Nodes/getConnectedChildNodes";
import { v4 as uuidv4 } from "uuid";
import { useNodesPositionContext } from "./NodesPositionContext";

interface NodeTypes {
  id: string;
  type: string;
  data: { label: string; content?: string };
  width?: number;
  height?: number;
}

interface NodeMethod {
  (sourceId: string, ...args: any[]): void;
}

const FlowDataContext = createContext<any>({
  nodes: [] as NodeTypes[],
  edges: [],
  setNodes: (nodes: any) => {},
  setEdges: (edges: any) => {},
  onNodesChange: () => {},
  onEdgesChange: () => {},
});
export const useFlowDataContext = () => useContext(FlowDataContext);

const FlowDataProvider = ({ children }: { children: ReactNode }) => {
  const flow_type = sessionStorage.getItem("flow_type") || "";
  const {updateNodesPosition, nodesPositionMap, deleteChildPsoitions} = useNodesPositionContext();

  const [nodes, setNodes, onNodesChange] = useNodesState<any>(InitialNodesEdgesByType?.[flow_type]?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>(InitialNodesEdgesByType?.[flow_type]?.edges || []);
  
  const [nodeCount, setNodeCount] = useState(0);

  useEffect(() => {
    setNodes((current: any) => {
      return current.map((node: any) => ({
        ...node,
        data: {...node.data, ...getNodeMethods({methodType: node.type, ndsPositionMap: nodesPositionMap})}
      }))
    })
  }, [nodeCount])
  
  const handleOnNodeAdd = useCallback(
    (sourceId: any, type: string, ndsPositionMap: any) => {
      /* Node Creation */
      const ids = {
        question: uuidv4(),
        addButton: uuidv4(),
        description: uuidv4(),
        uploadButton: uuidv4(),
        upload: uuidv4(),
        steps: uuidv4(),
        yes: uuidv4(),
        no: uuidv4(),
        qa: uuidv4(),
        link: uuidv4(),
      };

      let yesNode: any,
        noNode: any,
        paragraphNode: any,
        questionNode: any,
        stepNode: any,
        qaNode: any,
        addButtonNode: any,
        uploadButtonNode: any,
        uploadNode: any,
        linkNode: any;
      let yesPosition = { x: 0, y: 0 };
      let noPosition = { x: 0, y: 0 };
      let paragraphPosition = { x: 0, y: 0 };
      let stepsPosition = { x: 0, y: 0 };
      let questionPosition = { x: 0, y: 0 };
      let addButtonPosition = { x: 0, y: 0 };
      let qaPosition = { x: 0, y: 0 };
      let uploadButtonPosition = { x: 0, y: 0 };
      let uploadPosition = { x: 0, y: 0 };
      let LinkPosition = { x: 0, y: 0 };

      // Find Source node position
      const sourceNodePosition: any = ndsPositionMap?.[sourceId];

      if (sourceNodePosition) {
        yesPosition = {
          x: sourceNodePosition?.x + 250,
          y: sourceNodePosition?.y - 300,
        };

        noPosition = {
          x: sourceNodePosition?.x + 250,
          y: sourceNodePosition?.y + 300,
        };

        paragraphPosition = {
          x: sourceNodePosition?.x + 250,
          y: sourceNodePosition?.y - 61.5,
        };

        stepsPosition = {
          x: sourceNodePosition?.x + 250,
          y: sourceNodePosition?.y - 61.5,
        };

        qaPosition = {
          x: sourceNodePosition?.x + 170,
          y: sourceNodePosition?.y - 3,
        };

        questionPosition = {
          x: sourceNodePosition?.x + 400,
          y: sourceNodePosition?.y - 64,
        };

        if (type === "answer") {
          addButtonPosition = {
            x: sourceNodePosition?.x + 200,
            y: sourceNodePosition?.y - 3,
          };
        } else {
          addButtonPosition = {
            x: sourceNodePosition?.x + 750,
            y: sourceNodePosition?.y - 2.6,
          };
        }

        if (type === "steps") {
          uploadButtonPosition = {
            x: sourceNodePosition?.x + 700,
            y: sourceNodePosition?.y,
          };
        } else if (type === "answer") {
          uploadButtonPosition = {
            x: sourceNodePosition?.x + 770,
            y: sourceNodePosition?.y,
          };
        } else {
          uploadButtonPosition = {
            x: sourceNodePosition?.x + 600,
            y: sourceNodePosition?.y,
          };
        }

        uploadPosition = {
          x: sourceNodePosition?.x + 250,
          y: sourceNodePosition?.y - 65,
        };

        LinkPosition = {
          x: sourceNodePosition?.x + 250,
          y: sourceNodePosition?.y - 138,
        };
      }

      // const nodeMethods = getNodeMethods({ methodType: type, ndsPositionMap });

      if (type === "yesNo") {
        yesNode = {
          id: ids.yes,
          type: "choiceNode",
          position: yesPosition,
          data: { label: "yes", ...getNodeMethods({ methodType: 'choiceNode', ndsPositionMap }) },
        };

        noNode = {
          id: ids.no,
          type: "choiceNode",
          position: noPosition,
          data: { label: "no", ...getNodeMethods({ methodType: 'choiceNode', ndsPositionMap }) },
        };

        setNodes((nds: any) => [...nds, yesNode, noNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `e${sourceId}-${ids.yes}`,
            source: sourceId,
            target: ids.yes,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
          {
            id: `e${sourceId}-${ids.no}`,
            source: sourceId,
            target: ids.no,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.yes]: yesPosition, [ids.no]: noPosition });
      } else if (type === "paragraph") {
        paragraphNode = {
          id: ids.description,
          type: "descriptionNode",
          position: paragraphPosition,
          data: { label: "paragraph", ...getNodeMethods({ methodType: 'descriptionNode', ndsPositionMap }) },
        };

        uploadButtonNode = {
          id: ids.uploadButton,
          type: "uploadButtonNode",
          position: uploadButtonPosition,
          data: { label: "uploadButtonNode",  ...getNodeMethods({ methodType: 'uploadButtonNode', ndsPositionMap })  },
        };
        setNodes((nds: any) => [...nds, paragraphNode, uploadButtonNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `e${sourceId}-${ids.description}`,
            source: sourceId,
            target: ids.description,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
          {
            id: `e${ids.description}-${ids.uploadButton}`,
            source: ids.description,
            target: ids.uploadButton,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.description]: paragraphPosition, [ids.uploadButton]: uploadButtonPosition });
      } else if (type === "question") {
        qaNode = {
          id: ids.qa,
          type: "qaNode",
          position: qaPosition,
          data: { label: "question", ...getNodeMethods({ methodType: 'qaNode', ndsPositionMap }) },
        };

        questionNode = {
          id: ids.question,
          type: "questionNode",
          position: questionPosition,
          data: {...getNodeMethods({ methodType: 'questionNode', ndsPositionMap })},
        };

        addButtonNode = {
          id: ids.addButton,
          type: "addButtonNode",
          position: addButtonPosition,
          data: {...getNodeMethods({ methodType: 'addButtonNode', ndsPositionMap })},
        };

        setNodes((nds: any) => [...nds, qaNode, questionNode, addButtonNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `${sourceId}-${ids.qa}`,
            source: sourceId,
            target: ids.qa,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
          {
            id: `${ids.qa}-${ids.question}`,
            source: ids.qa,
            target: ids.question,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
          {
            id: `${ids.question}-${ids.addButton}`,
            source: ids.question,
            target: ids.addButton,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.qa]: qaPosition, [ids.question]: questionPosition, [ids.addButton]: addButtonPosition });
      } else if (type === "answer") {
        addButtonNode = {
          id: ids.addButton,
          type: "addButtonNode",
          position: addButtonPosition,
          data: { label: "addButtonNode", ...getNodeMethods({ methodType: 'addButtonNode', ndsPositionMap }) },
        };

        setNodes((nds: any) => [...nds, addButtonNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `${sourceId}-${ids.addButton}`,
            source: sourceId,
            target: ids.addButton,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.addButton]: addButtonPosition });
      } else if (type === "steps") {
        stepNode = {
          id: ids.steps,
          type: "stepsNode",
          position: stepsPosition,
          data: { label: "stepsNode", ...getNodeMethods({ methodType: 'stepsNode', ndsPositionMap }) },
        };

        uploadButtonNode = {
          id: ids.uploadButton,
          type: "uploadButtonNode",
          position: uploadButtonPosition,
          data: { label: "uploadButtonNode", ...getNodeMethods({ methodType: 'uploadButtonNode', ndsPositionMap }) },
        };

        setNodes((nds: any) => [...nds, stepNode, uploadButtonNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `e${sourceId}-${ids.steps}`,
            source: sourceId,
            target: ids.steps,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
          {
            id: `${ids.steps}-${ids.uploadButton}`,
            source: ids.steps,
            target: ids.uploadButton,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.steps]: stepsPosition, [ids.uploadButton]: uploadButtonPosition });
      } else if (type === "upload") {
        uploadNode = {
          id: ids.upload,
          type: "uploadNode",
          position: uploadPosition,
          data: {...getNodeMethods({ methodType: 'uploadNode', ndsPositionMap })},
        };

        setNodes((nds: any) => [...nds, uploadNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `e${sourceId}-${ids.upload}`,
            source: sourceId,
            target: ids.upload,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.upload]: uploadPosition });
      } else if (type === "link") {
        linkNode = {
          id: ids.link,
          type: "linkNode",
          position: LinkPosition,
          data: {...getNodeMethods({ methodType: 'linkNode', ndsPositionMap })},
        };

        setNodes((nds: any) => [...nds, linkNode]);
        setEdges((eds: any) => [
          ...eds,
          {
            id: `e${sourceId}-${ids.link}`,
            source: sourceId,
            target: ids.link,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
        updateNodesPosition({ [ids.link]: LinkPosition });
      } else {
        return; // Only handle yesNo type for this function
      }
      setNodeCount((prev) => prev + 2);
    },
    [setEdges, setNodes, updateNodesPosition]
  );

  const handleOnRemoveNodeConnection = useCallback(
    (sourceId: string) => {
      // Depending on the external Edges are giving outdated values. 
      // So, picking the functioal values and updateing the nodes and edges inside the setEdges function
      setEdges((eds: any) => {
        const childNodes = getConnectedChildNodes(sourceId, eds);
  
        // Remove child edges
        setNodes((nds: any) =>
          nds?.filter((node: any) => !childNodes.includes(node.id))
        );
        
        deleteChildPsoitions(childNodes);
  
        return eds?.filter(
          (edge: any) =>
            !childNodes.includes(edge.target) && edge.source !== sourceId
        );
      });
    },
    [setEdges, setNodes]
  );

  const handleOnNodeDelete = useCallback(
    (sourceId: string) => {
      setEdges((eds: any) => {
        const updatedEdges = eds?.filter(
          (edge: any) =>
            edge.source !== sourceId && edge.target !== sourceId
        );
  
        const connectedNodes = getConnectedNodesWithSource(sourceId, eds); // use original edges here
  
        // Now update nodes inside this block
        setNodes((nds: any) =>
          nds?.filter((node: any) => !connectedNodes.includes(node.id))
        );
        deleteChildPsoitions(connectedNodes);
  
        return updatedEdges;
      });
    },
    [setEdges, setNodes]
  );

  const handleOnContentUpdate = useCallback(
    (nodeId: any, newContent: any) => {
      setNodes((nds: any) =>
        nds.map((node: any) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, content: newContent } }
            : node
        )
      );
    },
    []
  );

  const handleOnStepsUpdate = useCallback(
    (nodeId: string, updatedSteps: string[]) => {
      setNodes((prevNodes: any) =>
        prevNodes.map((node: any) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, content: updatedSteps } }
            : node
        )
      );
    },
    []
  );

  //De-Link Node
  const handleOnNodeDeLink = useCallback((currentNodeId: string) => {
    // First, find any edge where the target is the current node
    const incomingEdge = edges.find((edge: any) => edge.target === currentNodeId);

    if (incomingEdge) {
      const sourceNodeId = incomingEdge.source;
      const sourceNode = nodes.find((node: any) => node.id === sourceNodeId);

      // Check if the source node is a qaNode
      if (sourceNode?.type === "qaNode") {
        // If it's a qaNode, remove the qaNode itself
        setNodes((prevNodes: any) =>
          prevNodes.filter((node: any) => node.id !== sourceNodeId)
        );

        // Also remove any edges connected to the qaNode
        setEdges((prevEdges: any) =>
          prevEdges.filter((edge: any) =>
            edge.source !== sourceNodeId && edge.target !== sourceNodeId
          )
        );
      }
    }

    // Original functionality - remove the edge connecting to the current node
    setEdges((prevEdges: any) =>
      prevEdges.filter((edge: any) => edge.target !== currentNodeId)
    );
  }, [edges, nodes]);
  
  const getNodeMethods = useCallback(({methodType, ndsPositionMap}: {methodType: string; ndsPositionMap?: Record<string, { x: number; y: number }>}): NodeMethod[] => {
    const onAdd: NodeMethod = (sourceId: string, sourceType: string) => handleOnNodeAdd(sourceId, sourceType, ndsPositionMap);
    const onRemoveConnected: NodeMethod = (sourceId: string) => handleOnRemoveNodeConnection(sourceId);
    const onRemoveSingleConnected: NodeMethod = (sourceId: string) => handleOnNodeDelete(sourceId);
    const onDeLink: NodeMethod = (sourceId: string) => handleOnNodeDeLink(sourceId);
    const onContentUpdate: NodeMethod = (sourceId: string, newContent: string) => handleOnContentUpdate(sourceId, newContent);
    const onStepsUpdate: NodeMethod = (sourceId: string, updatedSteps: string[]) => handleOnStepsUpdate(sourceId, updatedSteps);

    const commonMethods: any = {
      onAdd,
      onRemoveConnected,
      onRemoveSingleConnected,
      onDeLink,
      onContentUpdate,
    };
  
    const methodsMapByNodeType: any = {
      questionNode: commonMethods,
      addButtonNode: commonMethods,
      descriptionNode: commonMethods,
      uploadButtonNode: commonMethods,
      uploadNode: commonMethods,
      choiceNode: commonMethods,
      qaNode: commonMethods,
      linkNode: commonMethods,
      stepsNode: {
        ...commonMethods,
        onStepsUpdate,
      },
    };
  
    return methodsMapByNodeType[methodType] || {};
  }, [handleOnContentUpdate, handleOnNodeAdd, handleOnNodeDeLink, handleOnNodeDelete, handleOnRemoveNodeConnection, handleOnStepsUpdate]);

  const contextValue = useMemo(
    () => ({
      nodes,
      edges,
      setNodes,
      setEdges,
      onNodesChange,
      onEdgesChange,
      getNodeMethods,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, edges]
  );

  return (
    <FlowDataContext.Provider value={contextValue}>
      {children}
    </FlowDataContext.Provider>
  );
};

export default memo(FlowDataProvider);
